import React, { useState, useEffect } from 'react';

import { IconDeviceGamepad2, IconCircleCheckFilled } from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import EmptyState from '@/components/EmptyState';
import GameDetailsDrawerSkeleton from '@/components/SkeletonLoader/GameDetailsDrawerSkeleton';
import TagCapsule from '@/components/TagCapsule';
import { LessonStageConfig } from '@/constants/constant';
import { ELessonStageType } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import { getParentChildGameDetail } from '@/service/dashboardService';
import { useParentStore } from '@/store/useParentStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { IParentGame, IParentGameDetail } from '@/types/parent';
import { formatUnixToLongDate, formatSecondsToDuration } from '@/utils';

interface IGameDetailsDrawerProps {
  game: IParentGame | null;
  onClose: () => void;
}

const GameDetailsDrawer: React.FC<IGameDetailsDrawerProps> = ({
  game,
  onClose,
}) => {
  const { formatText } = useFormatText();
  const { selectedChild } = useParentStore();
  const { settings } = useSettingsStore();
  const language = settings?.language;
  const selectedChildId = selectedChild?.id;
  const [gameDetail, setGameDetail] = useState<IParentGameDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchGameDetail = async () => {
      if (!game?.id || !selectedChildId) return;

      setIsLoading(true);
      try {
        const response = await getParentChildGameDetail(
          selectedChildId,
          game.id
        );
        if (response?.status) {
          setGameDetail(response?.data);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchGameDetail();
  }, [game?.id, selectedChildId]);

  const config = gameDetail?.stage
    ? LessonStageConfig[gameDetail.stage as ELessonStageType]
    : null;

  if (!game) return null;

  return (
    <CustomModal
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
      onClose={onClose}
      position="right"
      isHeader
      heading={formatText('parent-dashboard.game-details.title')}
      buttonClassName="justify-end"
      childClassName="!items-start gap-6"
      dataTestId="drawer"
    >
      {isLoading ? (
        <GameDetailsDrawerSkeleton />
      ) : (
        <>
          {/* Overview Section */}
          <section className="flex w-full flex-col gap-4">
            <h3 className="text-base font-semibold text-neutral-primary">
              {formatText('parent-dashboard.game-details.overview')}
            </h3>

            {/* Game card */}
            <div className="flex flex-col gap-6 rounded-2xl border border-neutral-light-200 p-4">
              {/* Name + stage row */}
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center rounded-lg bg-brand-default p-2">
                  <IconDeviceGamepad2 size={24} className="text-white" />
                </div>
                <div className="flex flex-1 flex-wrap items-center gap-2">
                  <span className="text-lg font-semibold text-neutral-primary">
                    {language === 'ar'
                      ? (gameDetail?.title_ar ?? game?.title_ar)
                      : (gameDetail?.title_en ?? game?.title_en)}
                  </span>

                  {gameDetail?.quarters !== null &&
                    gameDetail?.quarters !== undefined && (
                      <TagCapsule
                        value={`Q${gameDetail.quarters}`}
                        className="bg-purple-50 text-brand-default"
                      />
                    )}
                  {gameDetail?.rounds !== null &&
                    gameDetail?.rounds !== undefined && (
                      <TagCapsule
                        value={`R${gameDetail.rounds}`}
                        className="bg-neutral-100 text-neutral-primary"
                      />
                    )}
                  <TagCapsule
                    value={config?.label ? formatText(config.label) : ''}
                    className={config?.className}
                  />
                </div>
              </div>

              {/* Stat cards */}
              <div className="grid w-full grid-cols-3 gap-4">
                {/* Plays */}
                <div className="flex flex-col items-center rounded-lg bg-[#fef3c7] px-4 py-2">
                  <span
                    className="mb-0.5 text-xs font-semibold"
                    style={{ color: '#d97706' }}
                  >
                    {formatText('parent-dashboard.game-details.plays')}
                  </span>
                  <span
                    className="text-2xl font-semibold"
                    style={{ color: '#7c2d12' }}
                  >
                    {gameDetail?.played_count ?? 0}
                  </span>
                </div>

                {/* Time */}
                <div className="flex flex-col items-center rounded-lg bg-success-light-50 px-4 py-2">
                  <span className="mb-0.5 text-xs font-semibold text-success-default">
                    {formatText('parent-dashboard.game-details.time')}
                  </span>
                  <span
                    className="text-2xl font-semibold"
                    style={{ color: '#14532d' }}
                  >
                    {formatSecondsToDuration(gameDetail?.total_time_seconds)}
                  </span>
                </div>

                {/* Accuracy */}
                <div
                  className="flex flex-col items-center rounded-lg px-4 py-2"
                  style={{ backgroundColor: '#eef2ff' }}
                >
                  <span
                    className="mb-0.5 text-xs font-semibold"
                    style={{ color: '#4f46e5' }}
                  >
                    {formatText('parent-dashboard.game-details.accuracy')}
                  </span>
                  <span
                    className="text-2xl font-semibold"
                    style={{ color: '#312e81' }}
                  >
                    {gameDetail?.avg_accuracy ?? 0}%
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Activity Timeline */}
          <section className="inline-flex w-full flex-col gap-4">
            <div className="border-b border-neutral-200 pb-1">
              <h3 className="text-base font-semibold leading-7 text-neutral-primary">
                {formatText('parent-dashboard.game-details.activity-timeline')}
              </h3>
            </div>

            <div className="relative flex flex-col gap-6">
              {gameDetail?.activity_timeline &&
              gameDetail.activity_timeline.length > 0 ? (
                gameDetail?.activity_timeline?.map((entry, index) => (
                  <React.Fragment key={index}>
                    {/* Vertical line */}
                    <div className="absolute left-5 top-0 h-full w-px bg-neutral-200 rtl:left-auto rtl:right-5" />
                    <div className="flex items-center gap-4">
                      {/* Timeline Dot */}
                      <div className="relative z-10 flex items-center justify-center rounded-full border border-neutral-300 bg-white p-2">
                        <IconCircleCheckFilled
                          size={24}
                          className="text-brand-default"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex flex-1 items-center justify-between gap-2">
                        <span className="text-base font-semibold text-neutral-primary">
                          {formatUnixToLongDate(entry?.date)}
                        </span>
                        <span className="shrink-0 text-sm text-neutral-secondary">
                          {formatSecondsToDuration(entry?.time_spent_seconds)}
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                ))
              ) : (
                <EmptyState
                  imageSrc="/assets/images/lesson-breakdown-empty.png"
                  imageAlt="no-data"
                  title={formatText('common.no-data-found')}
                  className="h-100!"
                  width={120}
                  height={120}
                />
              )}
            </div>
          </section>
        </>
      )}
    </CustomModal>
  );
};

export default GameDetailsDrawer;
